import { useState, useCallback, useEffect } from "react";
import CustomModal from "../../Common/CustomModal";
import { saveOrganization, getMuseum, fileUpload } from "../../../api";
import CreatableSelect from "react-select/creatable";
import { withAsyncPaginate } from "react-select-async-paginate";
import { toast } from "react-toastify";
import { getReactSelectThemeProps } from "../../../helpers/reactSelectThemeStyles";
import "./AddOrganizationModal.scss";

const reactSelectThemeProps = getReactSelectThemeProps();
const AsyncCreatablePaginate = withAsyncPaginate(CreatableSelect);
const PAGE_SIZE = 12;

function flattenMuseumResults(results = []) {
  const options = [];
  for (const museo of results) {
    options.push(museumToOption(museo));
    const children = Array.isArray(museo.children) ? museo.children : [];
    for (const child of children) {
      options.push(museumToOption(child));
    }
  }
  return options;
}

function museumToOption(museo) {
  return {
    value: museo.id,
    label: museo.denominazione,
    museo,
  };
}

function isCustomOrganizationOption(option) {
  if (!option) return false;
  if (option.__isNew__ === true || option.isCustom === true) return true;
  if (typeof option.value === "string" && option.value.startsWith("__custom__:")) {
    return true;
  }
  return !option.museo;
}

function getCustomOrganizationName(option) {
  if (typeof option.value === "string" && option.value.startsWith("__custom__:")) {
    return option.value.slice("__custom__:".length);
  }
  return option.label?.trim() || "";
}

function formatMuseumOption(option) {
  if (isCustomOrganizationOption(option)) {
    return <span>{getCustomOrganizationName(option) || option.label}</span>;
  }
  const museo = option.museo;
  return (
    <div className="organization-option">
      {museo?.immagine_copertina && (
        <img
          className="organization-option__thumb"
          src={museo.immagine_copertina}
          alt=""
        />
      )}
      <div className="organization-option__text">
        <span className="organization-option__name">{option.label}</span>
        {museo?.nome_comune && (
          <span className="organization-option__comune">{museo.nome_comune}</span>
        )}
      </div>
    </div>
  );
}

const AddOrganizationModal = ({ isOpen, onClose, fetchOrganizations }) => {
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isCustomOrganization, setIsCustomOrganization] = useState(false);
  const [organizationData, setOrganizationData] = useState({
    id: null,
    name: "",
    comune: "",
    thumbnailUrl: "",
  });
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedOrganization(null);
    setIsCustomOrganization(false);
    setOrganizationData({
      id: null,
      name: "",
      comune: "",
      thumbnailUrl: "",
    });
    setLogoUrl("");
    setLogoPreview("");
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const loadOrganizationOptions = useCallback(async (search, _prevOptions, { page = 1 } = {}) => {
    const query = search?.trim();
    if (!query) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }

    try {
      const response = await getMuseum({
        page,
        pageSize: PAGE_SIZE,
        ordering: "denominazione",
        orderDir: "asc",
        search: query,
      });

      return {
        options: flattenMuseumResults(response?.results),
        hasMore: response?.next != null,
        additional: { page: page + 1 },
      };
    } catch (error) {
      console.error("Error fetching public museo data", error);
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  }, []);

  const handleOrganizationChange = useCallback((option) => {
    setSelectedOrganization(option);

    if (!option) {
      setIsCustomOrganization(false);
      setOrganizationData({
        id: null,
        name: "",
        comune: "",
        thumbnailUrl: "",
      });
      return;
    }

    if (isCustomOrganizationOption(option)) {
      const name = getCustomOrganizationName(option);
      setIsCustomOrganization(true);
      setOrganizationData({
        id: null,
        name,
        comune: "",
        thumbnailUrl: "",
      });
      return;
    }

    const museo = option.museo;
    setIsCustomOrganization(false);
    setOrganizationData({
      id: museo.id,
      name: museo.denominazione,
      comune: museo.nome_comune || "",
      thumbnailUrl: museo.immagine_copertina || "",
    });
  }, []);

  const handleComuneChange = useCallback((value) => {
    setOrganizationData((prev) => ({
      ...prev,
      comune: value,
    }));
  }, []);

  const handleLogoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fileUpload(formData);
      const uploadedLogoUrl = response.id;
      setLogoUrl(uploadedLogoUrl);
      setLogoPreview(URL.createObjectURL(file));
      toast.success("Logo caricato con successo");
    } catch (error) {
      console.error("Error uploading logo", error);
      toast.error("Errore nel caricamento del logo");
    } finally {
      setIsUploadingLogo(false);
      event.target.value = "";
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!organizationData.name?.trim()) {
      toast.error("Seleziona o inserisci il nome dell'organizzazione");
      return;
    }

    if (!organizationData.comune?.trim()) {
      toast.error("Inserisci il comune");
      return;
    }


    if (!isCustomOrganization && !organizationData.id) {
      toast.error("Seleziona un'organizzazione dalla lista");
      return;
    }

    const requestData = {
      name: organizationData.name.trim(),
      comune: organizationData.comune.trim(),
      thumbnailUrl: organizationData.thumbnailUrl || "",
      logoUrl,
      ...(!isCustomOrganization ? { id: organizationData.id } : {}),
    };

    try {
      await saveOrganization(requestData);
      await fetchOrganizations();
      toast.success("Organizzazione salvata con successo");
      onClose();
    } catch (err) {
      console.error("Errore nella creazione dell'organizzazione", err);
      toast.error("Errore nella creazione dell'organizzazione");
    }
  }, [organizationData, logoUrl, isCustomOrganization, onClose, fetchOrganizations]);

  return (
    <CustomModal title={"Aggiungi nuova organizzazione"} isVisible={isOpen} onClose={onClose}>
      <div className="add-organization-modal-body">
        <div className="input-wrapper">
          <label htmlFor="organization-select" className="input-label">
            Nome organizzazione
          </label>
          <AsyncCreatablePaginate
            inputId="organization-select"
            className="organization-select"
            {...reactSelectThemeProps}
            value={selectedOrganization}
            loadOptions={loadOrganizationOptions}
            onChange={handleOrganizationChange}
            additional={{ page: 1 }}
            placeholder="Digita il nome dell'organizzazione..."
            isClearable
            debounceTimeout={300}
            formatOptionLabel={formatMuseumOption}
            formatCreateLabel={(inputValue) =>
              `Aggiungi nuova organizzazione: "${inputValue}"`
            }
            getNewOptionData={(inputValue) => ({
              label: inputValue.trim(),
              value: `__custom__:${inputValue.trim()}`,
              __isNew__: true,
              isCustom: true,
            })}
            isValidNewOption={(inputValue, _selectValue, options) => {
              const query = inputValue.trim().toLowerCase();
              if (!query) return false;
              return !options.some(
                (option) => option.label?.trim().toLowerCase() === query
              );
            }}
            noOptionsMessage={({ inputValue }) =>
              inputValue?.trim()
                ? "Nessuna organizzazione trovata"
                : "Digita per cercare un'organizzazione"
            }
          />
        </div>

        {organizationData.thumbnailUrl && !isCustomOrganization && (
          <div className="input-wrapper organization-thumbnail-preview">
            <span className="input-label">Anteprima copertina</span>
            <img src={organizationData.thumbnailUrl} alt="Anteprima organizzazione" />
          </div>
        )}

        <div className="input-wrapper">
          <label htmlFor="comune" className="input-label">
            Comune o sito
          </label>
          {isCustomOrganization ? (
            <input
              id="comune"
              type="text"
              name="comune"
              value={organizationData.comune}
              onChange={(e) => handleComuneChange(e.target.value)}
              placeholder="Inserisci il comune"
            />
          ) : (
            <p className="organization-comune-readonly">
              {organizationData.comune || "—"}
            </p>
          )}
        </div>

        <div className="input-wrapper">
          <label htmlFor="organization-logo" className="input-label">
            Logo organizzazione
          </label>
          <div className="organization-logo-upload">
            <label className="organization-logo-upload__button" htmlFor="organization-logo">
              {isUploadingLogo ? "Caricamento..." : "Carica logo"}
            </label>
            <input
              id="organization-logo"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLogoUpload}
              disabled={isUploadingLogo}
              hidden
            />
            {logoPreview && (
              <img
                className="organization-logo-upload__preview"
                src={logoPreview}
                alt="Anteprima logo"
              />
            )}
          </div>
        </div>
      </div>
      <footer className="modal-footer">
        <button
          type="submit"
          className="modal-footer-button"
          onClick={handleSubmit}
          disabled={isUploadingLogo}
        >
          Aggiungi Organizzazione
        </button>
      </footer>
    </CustomModal>
  );
};

export default AddOrganizationModal;
